// =============================================================================
// features/sales/agenda/hooks.ts — Mutações da agenda comercial (F33-S06):
// criar, concluir, reagendar e cancelar compromissos.
//
// Camada de dados separada da árvore visual (mesma decisão de
// hooks/sales/useAppointments.ts) para que o `ScheduleAppointmentDialog`
// (features/sales/components/, reutilizado fora da agenda em F33-S07/S08)
// consuma a mesma mutação de criação sem duplicar validação/idempotência.
//
// Idempotência (doc 26 §9, rotas complete/reschedule/cancel): cada submit
// gera um `Idempotency-Key` (UUID v4) novo — clique duplo do vendedor não
// conclui/reagenda/cancela duas vezes (mesmo padrão de
// features/billing/api.ts e hooks/simulator/useSendSimulation.ts).
//
// Nenhuma mutação faz rollback otimista: conclusão/reagendamento/
// cancelamento têm efeito de negócio real (outcome, novo compromisso,
// encerramento) — a tela espera a resposta do servidor antes de atualizar
// (nota do slot: "ação otimista só onde há reversão segura").
// =============================================================================

import type {
  Appointment,
  AppointmentCancelBody,
  AppointmentCompleteBody,
  AppointmentCompleteResponse,
  AppointmentCreateBody,
  AppointmentRescheduleBody,
  AppointmentRescheduleResponse,
} from '@elemento/shared-schemas';
import {
  AppointmentCompleteResponseSchema,
  AppointmentRescheduleResponseSchema,
  AppointmentSchema,
} from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { appointmentKeys } from '../../../hooks/sales/useAppointments';
import { api } from '../../../lib/api';

// ---------------------------------------------------------------------------
// Detalhe — GET /api/sales/appointments/:id
//
// Usado pelo AppointmentDrawer para resolver o compromisso SUBSTITUÍDO
// (`supersedesAppointmentId`) quando o compromisso aberto já é resultado de
// um reagendamento anterior — histórico visível, nunca escondido (doc 26
// §4.1 "reagendamento preserva autoria e histórico").
// ---------------------------------------------------------------------------

async function fetchAppointment(id: string): Promise<Appointment> {
  const raw = await api.get<unknown>(`/api/sales/appointments/${encodeURIComponent(id)}`);
  return AppointmentSchema.parse(raw);
}

export const appointmentDetailKey = (id: string) =>
  ['sales', 'appointments', 'detail', id] as const;

export function useAppointment(id: string | null): {
  appointment: Appointment | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery({
    queryKey: appointmentDetailKey(id ?? 'disabled'),
    queryFn: () => fetchAppointment(id as string),
    enabled: id !== null,
    staleTime: 30_000,
  });

  return { appointment: data, isLoading, isError };
}

// ---------------------------------------------------------------------------
// Criar — POST /api/sales/appointments (sem Idempotency-Key: rota de
// criação não exige o header, ver apps/api/.../sales/appointments/routes.ts)
// ---------------------------------------------------------------------------

async function createAppointment(body: AppointmentCreateBody): Promise<Appointment> {
  const raw = await api.post<unknown>('/api/sales/appointments', body);
  return AppointmentSchema.parse(raw);
}

export function useCreateAppointment(opts?: {
  onSuccess?: (appointment: Appointment) => void;
  onError?: (error: Error) => void;
}): {
  mutate: (body: AppointmentCreateBody) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: createAppointment,
    onSuccess: (appointment) => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      opts?.onSuccess?.(appointment);
    },
    onError: (error: Error) => opts?.onError?.(error),
  });

  return { mutate, isPending };
}

// ---------------------------------------------------------------------------
// Concluir — POST /api/sales/appointments/:id/complete
// ---------------------------------------------------------------------------

async function completeAppointment(
  id: string,
  body: AppointmentCompleteBody,
): Promise<AppointmentCompleteResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/appointments/${encodeURIComponent(id)}/complete`,
    body,
    {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    },
  );
  return AppointmentCompleteResponseSchema.parse(raw);
}

export function useCompleteAppointment(opts?: {
  onSuccess?: (response: AppointmentCompleteResponse) => void;
  onError?: (error: Error) => void;
}): {
  mutate: (input: { id: string; body: AppointmentCompleteBody }) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AppointmentCompleteBody }) =>
      completeAppointment(id, body),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      opts?.onSuccess?.(response);
    },
    onError: (error: Error) => opts?.onError?.(error),
  });

  return { mutate, isPending };
}

// ---------------------------------------------------------------------------
// Reagendar — POST /api/sales/appointments/:id/reschedule
// ---------------------------------------------------------------------------

async function rescheduleAppointment(
  id: string,
  body: AppointmentRescheduleBody,
): Promise<AppointmentRescheduleResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/appointments/${encodeURIComponent(id)}/reschedule`,
    body,
    { headers: { 'Idempotency-Key': crypto.randomUUID() } },
  );
  return AppointmentRescheduleResponseSchema.parse(raw);
}

export function useRescheduleAppointment(opts?: {
  onSuccess?: (response: AppointmentRescheduleResponse) => void;
  onError?: (error: Error) => void;
}): {
  mutate: (input: { id: string; body: AppointmentRescheduleBody }) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AppointmentRescheduleBody }) =>
      rescheduleAppointment(id, body),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      opts?.onSuccess?.(response);
    },
    onError: (error: Error) => opts?.onError?.(error),
  });

  return { mutate, isPending };
}

// ---------------------------------------------------------------------------
// Cancelar — POST /api/sales/appointments/:id/cancel
// ---------------------------------------------------------------------------

async function cancelAppointment(id: string, body: AppointmentCancelBody): Promise<Appointment> {
  const raw = await api.post<unknown>(
    `/api/sales/appointments/${encodeURIComponent(id)}/cancel`,
    body,
    {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    },
  );
  return AppointmentSchema.parse(raw);
}

export function useCancelAppointment(opts?: {
  onSuccess?: (appointment: Appointment) => void;
  onError?: (error: Error) => void;
}): {
  mutate: (input: { id: string; body: AppointmentCancelBody }) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AppointmentCancelBody }) =>
      cancelAppointment(id, body),
    onSuccess: (appointment) => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      opts?.onSuccess?.(appointment);
    },
    onError: (error: Error) => opts?.onError?.(error),
  });

  return { mutate, isPending };
}
